import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import { getQuotationDraft, editSchedule } from "api";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import EditScheduleRequestSummary from "components/molecules/TopNav/provider/EditScheduleDialog/components/EditScheduleRequestSummary";
import { DraftDataType, modifyDraftData, parseDraftValues } from "helpers/quotationFormHelper";
import EditScheduleSidebar from "./components/EditScheduleSidebar";
import EditScheduleForm from "./components/EditScheduleForm";
import VesselScheduleForm from "./components/VesselScheduleForm";
import EditFlight from "./components/EditFlight";

interface IEditScheduleDialogProps {
  isOpen: boolean;
  close: () => void;
  refetchQuotationLists: Function;
  draftId: string;
  quotation: string;
}
function EditScheduleDialog(props: IEditScheduleDialogProps) {
  const { isOpen, close, refetchQuotationLists, draftId, quotation } = props;
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, reset, getValues } = methods;
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const [countries, setCountries] = useState<ICountries[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOceanFreight = ["ocean_freight"].includes(getValues("service.freight_mode"));
  const isAirFreight = ["air_charter", "obc", "nfo", "regular_air"].includes(getValues("service.freight_mode"));
  useEffect(() => {
    if (draftId) {
      getQuotationDraft(draftId)
        .then((res: any) => {
          const data: DraftDataType = res?.data?.data;
          reset(modifyDraftData(data));
        })
        .catch((err) => {
          errorToast(err);
        });
    }
  }, [draftId]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const { schedule } = parseDraftValues(getValues());
    editSchedule(quotation, schedule)
      .then(() => {
        close();
        refetchQuotationLists();
        toast(
          intl.formatMessage({
            id: "tracking.components.editScheduleDialog.success",
            defaultMessage: "Schedule updated successfully",
          }),
          {
            type: "success",
          },
        );
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog size="sm" title="Edit Schedule" show={isOpen} onHide={close}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ minWidth: 380 }}>
          <div>
            <div>
              <Row className="mb-1">
                <Col xs={12}>
                  <div className="flex-row flex-wrap">
                    <div className="w-200 pr-16">
                      <h6>
                        <FormattedMessage
                          id="tracking.components.editScheduleDialog.requestSummary"
                          defaultMessage="Request summary"
                        />
                      </h6>
                    </div>
                    <div style={{ width: "504px" }}>
                      <EditScheduleRequestSummary />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <hr style={{ margin: "16px 0px" }} />
            <div className="mt-4">
              <Row className="mb-1">
                <Col xs={12}>
                  <div className="flex-row flex-wrap">
                    <div className="w-200 pr-16">
                      <EditScheduleSidebar />
                    </div>
                    <div style={{ width: "504px" }}>
                      <EditScheduleForm countries={countries} />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            {isOceanFreight && <VesselScheduleForm countries={countries} />}
            {isAirFreight && <EditFlight countries={countries} />}
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex justify-content-between">
              <Button
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.discard", defaultMessage: "Discard" })}
                size="lg"
                onClick={close}
              />
              <Button isLoading={isSubmitting} variant="primary" type="submit" size="lg">
                {intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
              </Button>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
}
export default EditScheduleDialog;
